"use client";

import { use, useCallback, useState } from "react";

import type { PushNotificationRecordStatus } from "@/backend/focus";
import { upsertPushNotificationSubscription } from "@/backend/focus";
import { createToastErrorDetails } from "@/components/ui/toast-helpers";
import { useToast } from "@/components/ui/use-toast";
import { UserContext } from "@/contexts/user-context";
import {
  notificationsSupported,
  urlBase64ToUint8Array,
} from "@/utils/notifications";

import { useFingerprint } from "./use-fingerprint";
import { useServiceWorker } from "./use-service-worker";

export function usePushNotifications() {
  const { toast } = useToast();

  const { currentUserPublicKey } = use(UserContext);
  const [processing, setProcessing] = useState(false);
  const fingerPrintHash = useFingerprint();

  const { swRegistration } = useServiceWorker();

  const registerServiceWorkerPushManager = useCallback(async () => {
    if (!swRegistration || !process.env.NEXT_PUBLIC_WEB_PUSH_SERVER_KEY) {
      return;
    }

    const pushServerPublicKey = process.env.NEXT_PUBLIC_WEB_PUSH_SERVER_KEY;
    const applicationServerKey = urlBase64ToUint8Array(pushServerPublicKey);

    // await new Promise((resolve) => {
    //   if (swRegistration.active) return resolve(true);
    //   swRegistration.addEventListener("ready", () => resolve(true));
    // });

    const subscription = await swRegistration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey,
    });

    return subscription.toJSON();
  }, [swRegistration]);

  const subscribeToPushNotifications = useCallback(
    async ({
      webPushSubscription,
      status,
    }: {
      webPushSubscription: PushSubscriptionJSON | undefined;
      status: PushNotificationRecordStatus;
    }) => {
      if (!webPushSubscription) {
        throw new Error("Push notification subscription is not defined");
      }

      if (!webPushSubscription.endpoint) {
        throw new Error("Push notification endpoint is not defined");
      }

      if (!webPushSubscription.keys) {
        throw new Error("Web Push Subscription keys are missing");
      }

      if (!currentUserPublicKey) {
        throw new Error("User is not logged in");
      }

      if (!fingerPrintHash) {
        throw new Error("Fingerprint hash is missing");
      }

      const payload = {
        UserPublicKey: currentUserPublicKey,
        UserAgent: navigator.userAgent,
        Endpoint: webPushSubscription.endpoint,
        AuthKey: webPushSubscription.keys.auth,
        P256dhKey: webPushSubscription.keys.p256dh,
        FingerPrint: fingerPrintHash,
        Status: status,
      };

      await upsertPushNotificationSubscription(payload);
    },
    [currentUserPublicKey, fingerPrintHash],
  );

  const subscribeToNotifications = async (
    status: PushNotificationRecordStatus,
  ) => {
    setProcessing(true);

    try {
      const permission = await window.Notification.requestPermission();

      if (permission === "granted") {
        const webPushSubscription = await registerServiceWorkerPushManager();
        await subscribeToPushNotifications({ webPushSubscription, status });
        toast({
          title: "Subscribed Successfully",
          variant: "success",
          description: "You will now receive payment notifications.",
        });
        return true;
      } else {
        toast({
          variant: "default",
          title: "Notification Preferences",
          description:
            "Opted out for now. You can change this anytime in your Settings.",
        });
        return false;
      }
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to subscribe to push notifications`,
        details: createToastErrorDetails(err),
      });
      return false;
    } finally {
      setProcessing(false);
    }
  };

  return {
    registerServiceWorkerPushManager,
    isSupported: notificationsSupported(),
    processing,
    subscribeToNotifications,
  };
}
